// MACHINERY V2 (Stroje) — "Premenovať dokument" (M2.5).
//
// Spec: data package §6 `[form] document name`; artboard 1005 (lower panel).
// Reference impl: components/manualsAndSoftware/ChangeDocumentDialog.tsx.
//
// IT RENAMES THE DOCUMENT, NOT THE FILE. `Document.Name` is user-supplied and
// separate from `File.Name`, which is read-only and shown only so the user knows
// which record they are editing — the artboard labels it "len na čítanie".
//
// THE FILE IS SENT BACK UNCHANGED. `ChangeDocumentViewModel` is `{ Name, File }`
// (§6), and v1 submits `{ name, file: document.file }`
// (ChangeDocumentDialog.tsx:56-60). Dropping `file` from the PUT would blank the
// attachment, so it rides along untouched — including the null case, which is a
// fileless document being renamed.
//
// FIX 4 lives in docsNameField.tsx — see that header for the full citation.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useEffect, useState } from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { ChangeDocument, Document } from "../../../models/machineDocumentsModel";
import TabDialog from "./TabDialog";
import DocNameField, { docNameError } from "./docsNameField";
import { FileTile } from "./docsRows";

export interface DocsRenameDialogV2Props {
  document: Document;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (documentId: string, model: ChangeDocument) => Promise<void>;
}

const DocsRenameDialogV2: React.FC<DocsRenameDialogV2Props> = ({
  document: doc,
  open,
  onOpenChange,
  onSubmit,
}) => {
  const { translate } = useTranslation("myMachinery");
  const [name, setName] = useState(doc.name || "");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  // Re-seed when the dialog is pointed at a different document (the host keeps
  // it mounted only while a target exists, but a fast Edit -> Edit on two rows
  // would otherwise keep the first name).
  useEffect(() => {
    setName(doc.name || "");
    setSubmitted(false);
  }, [doc.id, doc.name]);

  const save = async () => {
    setSubmitted(true);
    if (docNameError(name) !== null || busy) return;
    setBusy(true);
    try {
      await onSubmit(doc.id, { name: name.trim(), file: doc.file });
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <TabDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate("myMachinery:v2.tabs.docs.renameTitle")}
      width={480}
      footer={
        <>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {translate("common:buttons.cancel")}
          </button>
          <button type="button" className="btn btn-mod" onClick={save} disabled={busy}>
            {translate("common:buttons.save")}
          </button>
        </>
      }
    >
      {doc.file && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            padding: "7px 10px",
            borderRadius: 7,
            background: "var(--bg-soft)",
            border: "1px solid var(--border-soft)",
            marginBottom: 12,
            minWidth: 0,
          }}
        >
          <FileTile fileName={doc.file.name} />
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--muted)",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={doc.file.name}
          >
            {doc.file.name} · {translate("myMachinery:v2.tabs.docs.readOnly")}
          </span>
        </div>
      )}

      <DocNameField value={name} onChange={setName} showError={submitted} autoFocus />
    </TabDialog>
  );
};

export default DocsRenameDialogV2;
